/* ==========================================================================
   Collection grid behaviour.

   The markup already works without any of this: every filter is a checkbox in
   a GET form, every chip is a link, load-more is a link to page 2. What this
   adds is that a filter change swaps the results in place instead of
   reloading — which is the only way the panel can stay open while you filter.
   ========================================================================== */
(function () {
  'use strict';

  class CollectionGrid extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.panelOpen = false;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));

      /* A filtered URL that came from the back button has to repaint. */
      this.onPop = () => this.load(window.location.href, { push: false });
      window.addEventListener('popstate', this.onPop);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
    }

    /* ------------------------------------------------------------- helpers */
    get panel() { return this.querySelector('[data-col-panel]'); }
    get results() { return this.querySelector('[data-col-results]'); }

    setPanel(open) {
      const panel = this.panel;
      const toggle = this.querySelector('[data-col-toggle]');
      if (!panel) return;
      this.panelOpen = open;
      panel.hidden = !open;
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
    }

    /* The form's own state is the source of truth for a filter change: reading
       it back gives the same query string Shopify would have received from a
       plain submit. */
    formUrl() {
      const form = this.querySelector('[data-col-form]');
      if (!form) return null;
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
      const qs = params.toString();
      return form.getAttribute('action') + (qs ? '?' + qs : '');
    }

    /* ------------------------------------------------------------- events */
    onClick(event) {
      const toggle = event.target.closest('[data-col-toggle]');
      if (toggle) {
        event.preventDefault();
        this.setPanel(!this.panelOpen);
        return;
      }

      const apply = event.target.closest('[data-col-apply]');
      if (apply) {
        event.preventDefault();
        this.setPanel(false);
        return;
      }

      const more = event.target.closest('[data-col-more-link]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
        return;
      }

      const add = event.target.closest('[data-card-add]');
      if (add) {
        event.preventDefault();
        this.addToBag(add);
        return;
      }

      const link = event.target.closest('[data-col-link]');
      if (link && link.href) {
        event.preventDefault();
        this.load(link.href);
      }
    }

    onChange(event) {
      if (!event.target.closest('[data-col-form]')) return;
      const url = this.formUrl();
      if (url) this.load(url);
    }

    /* ------------------------------------------------- fetch and swap */
    async load(url, options) {
      const push = !options || options.push !== false;
      const target = new URL(url, window.location.origin);

      this.classList.add('is-loading');
      try {
        const fetchUrl = new URL(target);
        fetchUrl.searchParams.set('section_id', this.sectionId);
        const response = await fetch(fetchUrl.toString());
        if (!response.ok) throw new Error(response.statusText);
        const markup = await response.text();
        const fresh = new DOMParser().parseFromString(markup, 'text/html');
        const source = fresh.querySelector('[data-section-id="' + this.sectionId + '"]');
        if (!source) throw new Error('section missing from response');

        const wasOpen = this.panelOpen;
        const oldForm = this.querySelector('[data-col-form]');
        const scroll = oldForm ? oldForm.scrollTop : 0;

        this.innerHTML = source.innerHTML;

        /* The panel is inside the markup we just replaced, so its open state
           has to be re-applied rather than preserved. */
        this.setPanel(wasOpen);
        if (wasOpen) {
          const form = this.querySelector('[data-col-form]');
          if (form) form.scrollTop = scroll;
        }

        if (push) window.history.pushState({}, '', target.toString());
      } catch (error) {
        /* Fall back to a normal navigation rather than stranding the shopper. */
        window.location.href = target.toString();
      } finally {
        this.classList.remove('is-loading');
      }
    }

    async loadMore(button) {
      const items = this.querySelector('[data-col-items]');
      if (!items) return;

      button.classList.add('is-busy');
      try {
        const url = new URL(button.href, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(response.statusText);
        const fresh = new DOMParser().parseFromString(await response.text(), 'text/html');

        const nextItems = fresh.querySelector('[data-col-items]');
        if (nextItems) items.append(...nextItems.children);

        /* The next page's own load-more block already carries the right
           progress figures and the right next link — or the done state. */
        const nextMore = fresh.querySelector('[data-col-more]');
        const currentMore = this.querySelector('[data-col-more]');
        if (nextMore && currentMore) currentMore.replaceWith(nextMore);
      } catch (error) {
        window.location.href = button.href;
      } finally {
        button.classList.remove('is-busy');
      }
    }

    async addToBag(button) {
      const id = button.dataset.cardAdd;
      if (!id || !window.HM || !window.HM.Cart) return;

      const label = button.textContent.trim();
      button.disabled = true;
      try {
        await window.HM.Cart.add([{ id: Number(id), quantity: 1 }]);
        await window.HM.Cart.refresh();
        button.classList.add('is-added');
        button.textContent = button.dataset.addedLabel || 'Added';
        setTimeout(() => {
          button.classList.remove('is-added');
          button.textContent = label;
        }, 1800);
      } catch (error) {
        if (window.HM.toast) window.HM.toast(error.message);
      } finally {
        button.disabled = false;
      }
    }
  }

  if (!customElements.get('collection-grid')) {
    customElements.define('collection-grid', CollectionGrid);
  }
})();
